import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Clock, Star, MapPin, Hammer } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SITE, SERVICES } from "../lib/site";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";
import RecentJobBadge from "../components/RecentJobBadge";
import FeaturedProject from "../components/FeaturedProject";
import GoogleReviewsBadge from "../components/GoogleReviewsBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);

const HERO_BG = `${BACKEND}/api/uploads/dandy-welcome-hero.jpg`;

const TRUST = [
    { icon: ShieldCheck, label: "Fully Insured" },
    { icon: Award, label: "Workmanship Guarantee" },
    { icon: Clock, label: "On-Time, On-Budget" },
    { icon: Hammer, label: "Built Properly Since 2022" },
];

export default function Home() {
    const [projects, setProjects] = useState([]);
    const [testimonials, setTestimonials] = useState([]);

    useEffect(() => {
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
        api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <Seo path="/" />
            {/* HERO */}
            <section
                className="relative min-h-[88vh] flex items-end overflow-hidden grain"
                data-testid="hero-section"
            >
                <img
                    src={HERO_BG}
                    alt="Modern home extension by Dandy Builders"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="container-x relative z-10 pb-20 pt-32 md:pt-40 text-white">
                    <div className="max-w-3xl">
                        <div className="mb-6 flex flex-wrap gap-3">
                            <RecentJobBadge />
                            <GoogleReviewsBadge variant="inline" invert />
                        </div>
                        <span className="eyebrow !text-secondary">
                            Wokingham · Berkshire · est. {SITE.foundedYear}
                        </span>
                        <h1
                            className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02]"
                            data-testid="hero-heading"
                        >
                            Crafted by hand.<br />
                            <span className="text-secondary">Built to last.</span>
                        </h1>
                        <p className="mt-7 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                            Your friendly local builder for extensions, loft conversions,
                            groundworks, electrical and mechanical works across Berkshire.
                            Honest pricing, tidy sites, proper craftsmanship.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/quote"
                                className="btn-secondary"
                                data-testid="hero-quote-btn"
                            >
                                Get a Free Quote
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/services"
                                className="btn-outline-light"
                                data-testid="hero-services-btn"
                            >
                                Our Services
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="bg-muted border-y border-border">
                <div className="container-x py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {TRUST.map(({ icon: Icon, label }) => (
                        <div
                            key={label}
                            className="flex items-center gap-3"
                            data-testid={`trust-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <Icon className="text-primary" size={22} strokeWidth={1.5} />
                            <span className="font-medium text-sm md:text-base text-foreground">
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="section-pad container-x grid md:grid-cols-12 gap-12 items-center">
                <div className="md:col-span-6 order-2 md:order-1">
                    <SectionHeader
                        eyebrow="About Dandy Builders"
                        title="A proper local team that turns up, finishes the job, and answers the phone."
                        lead="We started Dandy Builders & Property Care in 2022 because we wanted to do things the right way — fixed quotes, no surprises, clean sites and a friendly face on the door. From a single rear extension to a full period restoration, we handle the paperwork and the hammers."
                    />
                    <Link
                        to="/about"
                        className="btn-outline mt-8"
                        data-testid="home-about-cta"
                    >
                        Meet the team <ArrowRight size={18} />
                    </Link>
                </div>
                <div className="md:col-span-6 order-1 md:order-2">
                    <div className="relative">
                        <img
                            src={`${BACKEND}/api/uploads/dandy-building-team.jpg`}
                            alt="Dandy Builders craftsman"
                            className="w-full rounded-lg shadow-lg aspect-[4/5] object-cover"
                        />
                        <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-lg shadow-xl max-w-[220px] hidden md:block">
                            <div className="text-4xl font-heading font-extrabold">3+</div>
                            <div className="text-sm opacity-90 mt-1">
                                years building across Wokingham & Berkshire
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="section-pad bg-muted">
                <div className="container-x">
                    <SectionHeader
                        eyebrow="What We Do"
                        title="Everything from foundations to finish."
                        lead="One team. Eight specialisms. No subbing your job out to strangers."
                    />
                    <div className="mt-14 grid md:grid-cols-3 lg:grid-cols-4 gap-5">
                        {SERVICES.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    to={`/services#${s.slug}`}
                                    key={s.slug}
                                    className="card-earthy group"
                                    data-testid={`service-card-${s.slug}`}
                                >
                                    <Icon
                                        className="text-primary group-hover:text-secondary transition-colors"
                                        size={28}
                                        strokeWidth={1.5}
                                    />
                                    <h3 className="mt-5 text-xl font-heading font-bold tracking-tight">
                                        {s.name}
                                    </h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                        {s.short}
                                    </p>
                                    <div className="mt-5 text-sm font-medium text-primary inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                        Learn more <ArrowRight size={14} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FEATURED PROJECT (renders only when one is flagged in admin) */}
            <FeaturedProject />

            {/* PROJECTS PREVIEW */}
            <section className="section-pad container-x">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHeader
                        eyebrow="Recent Work"
                        title="Built across Berkshire."
                    />
                    <Link to="/projects" className="btn-outline" data-testid="home-projects-cta">
                        View all projects <ArrowRight size={18} />
                    </Link>
                </div>
                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    {projects.slice(0, 6).map((p, i) => (
                        <Link
                            to="/projects"
                            key={p.id}
                            data-testid={`home-project-${p.id}`}
                            className={`group relative overflow-hidden rounded-lg ${
                                i === 0 ? "md:col-span-2 md:row-span-2" : ""
                            }`}
                        >
                            <img
                                src={resolveImg(p.image)}
                                alt={p.title}
                                className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                                    i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[4/3]"
                                }`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/30 to-transparent opacity-90" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                <div className="text-xs uppercase tracking-[0.2em] text-secondary opacity-95">
                                    {p.category}
                                </div>
                                <h3 className="mt-2 font-heading text-xl md:text-2xl font-bold leading-tight">
                                    {p.title}
                                </h3>
                                <div className="mt-1 text-sm opacity-80 flex items-center gap-1">
                                    <MapPin size={13} /> {p.location}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section-pad bg-primary text-primary-foreground">
                <div className="container-x">
                    <SectionHeader
                        eyebrow="What clients say"
                        title="Trusted by neighbours across Berkshire."
                        invert
                    />
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {testimonials.map((t) => (
                            <div
                                key={t.id}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-7 flex flex-col"
                                data-testid={`testimonial-${t.id}`}
                            >
                                <div className="flex gap-1 text-secondary">
                                    {Array.from({ length: t.rating }).map((_, i) => (
                                        <Star key={i} size={16} fill="currentColor" />
                                    ))}
                                </div>
                                <p className="mt-4 text-sm leading-relaxed text-white/90 flex-1">
                                    "{t.text}"
                                </p>
                                <div className="mt-6 pt-5 border-t border-white/10">
                                    <div className="font-heading font-bold">{t.name}</div>
                                    <div className="text-xs opacity-70 mt-0.5">
                                        {t.location} · {t.project}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Google Reviews CTA */}
                    <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 md:p-8" data-testid="google-reviews-cta">
                        <div className="flex items-center gap-5">
                            <div className="h-14 w-14 rounded-full bg-white grid place-items-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] opacity-70">Rated on Google</div>
                                <div className="mt-1 flex items-center gap-3">
                                    <span className="font-heading text-3xl font-extrabold">{SITE.googleRating.toFixed(1)}</span>
                                    <span className="flex items-center gap-0.5 text-amber-400">
                                        {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={16} fill="currentColor" />))}
                                    </span>
                                    <span className="text-sm opacity-80">· {SITE.googleReviewCount} reviews</span>
                                </div>
                            </div>
                        </div>
                        <a
                            href={SITE.googleReviewsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white text-primary px-6 py-3 rounded-md font-medium hover:bg-secondary hover:text-secondary-foreground transition-colors flex items-center gap-2"
                            data-testid="google-reviews-btn"
                        >
                            Read & write reviews on Google
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="section-pad container-x">
                <div className="bg-secondary text-secondary-foreground rounded-2xl p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight leading-tight">
                            Got a project in mind? Let's have a chat.
                        </h2>
                        <p className="mt-4 text-white/85">
                            Free site visit, no pressure, fixed-price quote in writing.
                            Call us, drop an email, or send the form and we'll be in touch.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-4 md:justify-end">
                        <Link to="/quote" className="bg-primary text-primary-foreground px-7 py-3.5 rounded-md font-medium hover:bg-primary-hover transition-colors" data-testid="cta-quote-btn">
                            Request a Quote
                        </Link>
                        <a href={SITE.phoneLink} className="border-2 border-white text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-secondary transition-colors" data-testid="cta-call-btn">
                            Call {SITE.phone}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
